import { useEffect, useMemo } from "react";
import {
    Container,
    Dialog,
    TextField,
    Button,
    Stack,
    MenuItem,
    Typography,
    Autocomplete,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    IconButton,
    Alert
} from "@mui/material";
import { ThumbDown, ThumbUp, Lightbulb, ContentPaste, Verified, ExpandMore, Help } from "@mui/icons-material";
import DOMPurify from "dompurify";
import { useState } from "react";
import styles from "./Answer.module.css";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { ChatAppResponse, VerifiedQuestion, getCitationFilePath, provideFeedbackApi, provideLikeDislikeApi, updateChat } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import {getActiveAccount, isDomain4User, isNonGpscUser, isSupplyChainUser, useAuthentication} from "../../authConfig";
import { Feedback, HtmlParsedAnswer } from "../../api";
import { getToken, useLogin } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { DomainOption as DomainOptionList } from "../../api/models";
import { DOMAIN_4_USER_ROLE, DOMAIN_USER_ROLE_ID_DICT, NON_GPSC_USER_ROLE, SUPPLYCHAIN_USER_ROLE } from "../../constants";

interface Props {
    answer: ChatAppResponse;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    sources: string[];
    showFollowupQuestions?: boolean;
    domainsList: DomainOptionList[];
}

export const Answer = ({
    answer,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    sources,
    showFollowupQuestions,
    domainsList
}: Props) => {
    const { isAdmin, isDeveloper, isDomainAdmin } = useAuthentication();
    const followupQuestions = answer.choices[0].context.followup_questions;
    const verifiedQuestions = answer.choices[0].verified_questions;
    const gm_verified = answer.choices[0].gm_verified;
    const messageContent = answer.choices[0].message.content;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(messageContent, isStreaming, onCitationClicked), [answer]);
    if (verifiedQuestions) {
        verifiedQuestions.forEach((fq: VerifiedQuestion, i) => {
            verifiedQuestions[i].htmlParsedAnswer = parseAnswerToHtml(fq.answer, isStreaming, onCitationClicked);
        }),
            [answer];
    }

    const [dislikeModalOpen, setDislikeModalOpen] = useState(false);
    const [likeModalOpen, setLikeModalOpen] = useState(false);
    const [generalFeedbackModalOpen, setGeneralFeedbackModalOpen] = useState(false);
    const [generalFeedback, setGeneralFeedback] = useState<string>("");
    const [feedback, setFeedback] = useState<Feedback>({
        is_harm: false,
        harm_reason: "",
        correct_answer: "",
        procedure_documents: [],
        additional: ""
    });

    type FeedbackErrors = {
        // harm_reason?: string;
        // correct_answer?: string;
    };
    const [errors, setErrors] = useState<FeedbackErrors>({});

    const [liked, setLiked] = useState<boolean>(false);
    const [disliked, setDisliked] = useState<boolean>(false);

    const [showFeedback, setShowFeedback] = useState(false);
    const client = useLogin ? useMsal().instance : undefined;
    const id_claims = client ? JSON.stringify(getActiveAccount(client)?.idTokenClaims ?? {}) : undefined;

    // This function determines when the citations should be disabled for domain user roles (SUPPLYCHAIN_USER_ROLE or DOMAIN_4_USER_ROLE)
    // If the user has SUPPLYCHAIN_USER_ROLE or DOMAIN_4_USER_ROLE, they should be able to see citations for their domain ONLY
    // If the user has BOTH SUPPLYCHAIN_USER_ROLE and DOMAIN_4_USER_ROLE, they should be able to see citations for BOTH domains
    // If the user has SUPPLYCHAIN_USER_ROLE or DOMAIN_4_USER_ROLE along with NON_GPSC_USER_ROLE, they should still be able to see citations for their domain ONLY
    const isCitationDisabledForDomainUserRole = (user: any, domainUserRole: string): boolean => {
        const allowedRoles = [SUPPLYCHAIN_USER_ROLE, DOMAIN_4_USER_ROLE, NON_GPSC_USER_ROLE];
        const userRoles = user?.idTokenClaims?.roles ?? [];
        const domainUserRoleIds = [];

        // Generate a list of domain ids for the domain user roles the user has (SUPPLYCHAIN_USER_ROLE or DOMAIN_4_USER_ROLE)
        for (let i = 0; i < userRoles.length; i++) {
            if (DOMAIN_USER_ROLE_ID_DICT.has(userRoles[i])) {
                domainUserRoleIds.push(DOMAIN_USER_ROLE_ID_DICT.get(userRoles[i]));                  
            }
        }

        // If the user has any role outside the allowedRoles, citations won't be disabled
        // This would occur when the user has a domain user role (SUPPLYCHAIN_USER_ROLE or DOMAIN_4_USER_ROLE) along with an ADMIN, IT, or regular USER role
        if (!userRoles.every((role: string) => allowedRoles.includes(role))) {
            return false;
        }

        // Check if the selected domain matches the domain user role the user has
        const selectedDomain = localStorage.getItem("selectedDomain") ?? "";
        const domainUserRoleId = DOMAIN_USER_ROLE_ID_DICT.get(userRoles.find((role: string) => role === domainUserRole) ?? domainUserRole);
        const domain = domainsList.find(domain => domain.domain_name === selectedDomain); 

        // If no matching domain found, or if the domain id does not exist in the domainUserRoleIds list, disable citations
        return !domainUserRoleId || !domain || !domainUserRoleIds.includes(String(domain.domain_id));
    };

    // Citation check
    if (parsedAnswer.citations.length > 0) {
        const realCitations = answer.choices[0].context.data_points.text.map((citation: string) => citation.split(":")[0].trim());
        if (parsedAnswer.citations.some((citation: string) => !realCitations.includes(citation))) {
            parsedAnswer.citations = realCitations;
        }
    }

    useEffect(() => {
        if (dislikeModalOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        // Clean up function
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [dislikeModalOpen]);

    useEffect(() => {
        if (likeModalOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        // Clean up function
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [likeModalOpen]);

    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const newErrors: FeedbackErrors = {};

        // TODO (NOTE): no need to check for errors if we are not using these fields in the feedback form anymore
        // if (feedback.is_harm && !feedback.harm_reason.trim()) {
        //     newErrors.harm_reason = "Harm reason is required";
        // }
        //
        // if (!feedback.correct_answer.trim()) {
        //     newErrors.correct_answer = "Correct answer is required";
        // }

        setErrors(newErrors);
        if (Object.keys(newErrors).length === 0) {
            await handleFeedback();
            setShowFeedback(true);

            setTimeout(() => {
                setShowFeedback(false);
            }, 2000);
            setDislikeModalOpen(false);
            // Reset feedback form
            setFeedback({
                is_harm: false,
                harm_reason: "",
                correct_answer: "",
                procedure_documents: [],
                additional: ""
            });
        }
    };

    const verifyQuestion = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const token = client ? await getToken(client) : undefined;
        await updateChat(token, answer.chat_id, {
            question: answer.choices[0].context.thoughts[0].description,
            answer: answer.choices[0].message.content,
            gm_verified: true
        });

        setLikeModalOpen(false);
    };

    const handleGeneralFeedback = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const token = client ? await getToken(client) : undefined;
        await updateChat(token, answer.chat_id, { additional: generalFeedback });
        setGeneralFeedbackModalOpen(false);
        setShowFeedback(true);

        setTimeout(() => {
            setShowFeedback(false);
        }, 2000);
        setGeneralFeedback("");
    };

    const handleLikeDislike = async (is_like: boolean) => {
        const token = client ? await getToken(client) : undefined;
        await provideLikeDislikeApi({ chat_id: answer.chat_id, is_like: is_like }, token);
    };

    const handleFeedback = async () => {
        const token = client ? await getToken(client) : undefined;
        await provideFeedbackApi({ chat_id: answer.chat_id, feedback: feedback }, token);
    };
    return (
        <div className={styles.boxContainer}>
            <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`}>
                <Dialog open={generalFeedbackModalOpen} onClose={() => setGeneralFeedbackModalOpen(false)} aria-labelledby={"General Feedback"}>
                    <Container>
                        <form onSubmit={handleGeneralFeedback} style={{ padding: "20px 40px", width: "540px" }}>
                            <Stack spacing={2}>
                                <Typography variant="h4">Additional Feedback (optional)</Typography>
                                <Typography variant="body2">Provide additional feedback on the quality of the chat or the answer provided.</Typography>
                                <TextField
                                    label="Additional Feedback"
                                    multiline
                                    fullWidth
                                    rows={3}
                                    placeholder="Enter your response here"
                                    value={generalFeedback}
                                    onChange={event => setGeneralFeedback(event.target.value || "")}
                                />
                                <Stack flexDirection="row" justifyContent="left">
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setGeneralFeedbackModalOpen(false);
                                        }}
                                        style={{ marginRight: "16px" }}
                                    >
                                        <Typography variant="button">Close</Typography>
                                    </Button>
                                    <Button variant="contained" type="submit">
                                        <Typography variant="button">Submit</Typography>
                                    </Button>
                                </Stack>
                            </Stack>
                        </form>
                    </Container>
                </Dialog>
                <Dialog open={likeModalOpen} onClose={() => setLikeModalOpen(false)} aria-labelledby={"Like Feedback"}>
                    <Container>
                        <form onSubmit={verifyQuestion} style={{ padding: "20px 40px", width: "540px" }}>
                            <Stack spacing={2}>
                                <Typography variant="h4">Would you like to make this a GM Verified Answer?</Typography>
                                <Stack flexDirection="row" justifyContent="left">
                                    <Button variant="outlined" onClick={() => setLikeModalOpen(false)} style={{ marginRight: "16px" }}>
                                        <Typography variant="button">Cancel</Typography>
                                    </Button>
                                    <Button variant="contained" type="submit">
                                        <Typography variant="button">Verify</Typography>
                                    </Button>
                                </Stack>
                            </Stack>
                        </form>
                    </Container>
                </Dialog>
                <Dialog open={dislikeModalOpen} onClose={() => setDislikeModalOpen(false)} aria-labelledby={"Dislike Feedback"}>
                    <Container>
                        <form onSubmit={handleFormSubmit} style={{ padding: "20px 40px", width: "540px" }}>
                            <Stack spacing={2}>
                                <h3>Other Feedback</h3>
                                <TextField
                                    label="Additional feedback on chat quality."
                                    multiline
                                    fullWidth
                                    rows={3}
                                    placeholder="Enter your response here"
                                    onChange={event => setFeedback({ ...feedback, additional: event.target.value || "" })}
                                />
                                <Stack flexDirection="row" justifyContent="left">
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setDislikeModalOpen(false);
                                        }}
                                        style={{ marginRight: "16px" }}
                                    >
                                        <Typography variant="button">Cancel</Typography>
                                    </Button>
                                    <Button variant="contained" type="submit">
                                        <Typography variant="button">Submit</Typography>
                                    </Button>
                                </Stack>
                            </Stack>
                        </form>
                    </Container>
                </Dialog>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <AnswerIcon />
                    <div>
                        {gm_verified ? (
                            <Verified color="primary" />
                        ) : (
                            <Stack direction="row">
                                {isDeveloper() && (
                                    <>
                                        <IconButton
                                            title="Show thought process"
                                            aria-label="Show thought process"
                                            onClick={() => onThoughtProcessClicked()}
                                            disabled={!answer.choices[0].context.thoughts?.length || isStreaming}
                                        >
                                            <Lightbulb />
                                        </IconButton>
                                        <IconButton
                                            title="Show supporting content"
                                            aria-label="Show supporting content"
                                            onClick={() => onSupportingContentClicked()}
                                            disabled={!answer.choices[0].context.data_points || isStreaming}
                                        >
                                            <ContentPaste />
                                        </IconButton>
                                    </>
                                )}
                                <IconButton
                                    title="Who to Go to For Help"
                                    aria-label="Who to Go to For Help"
                                    onClick={() => {
                                        const [basePath, hash] = getCitationFilePath("Who to Go to For Help.pdf#page=1").split("#");
                                        const selectedDomain = localStorage.getItem("selectedDomain") ?? "";
                                        const path = `${basePath}?domain=${encodeURIComponent(selectedDomain)}${hash ? "#" + hash : ""}`;
                                        onCitationClicked(path);
                                    }}
                                    disabled={isStreaming}
                                >
                                    <Help />
                                </IconButton>
                            </Stack>
                        )}
                    </div>
                </Stack>
                <Box flexGrow={1}>
                    <div className={styles.answerText} dangerouslySetInnerHTML={{ __html: sanitizedAnswerHtml }}></div>
                </Box>
                {!!parsedAnswer.citations.length && (
                    <>
                        <Stack direction="column" justifyContent="start" alignItems="start" flexWrap="wrap" spacing={1}>
                            <Typography variant="h4">Citations:</Typography>
                            {parsedAnswer.citations.map((x, i) => {
                                // Embed domain name in the citation file path, so we know in the backend which container to pull the blob from
                                const [basePath, hash] = getCitationFilePath(x).split("#");
                                const selectedDomain = localStorage.getItem("selectedDomain") ?? "";
                                const path = `${basePath}?domain=${encodeURIComponent(selectedDomain)}${hash ? "#" + hash : ""}`;
                                return (
                                    <Button 
                                        disabled={
                                            isNonGpscUser(getActiveAccount(client)) ||
                                            isStreaming ||
                                            (isSupplyChainUser(getActiveAccount(client)) && isCitationDisabledForDomainUserRole(getActiveAccount(client), SUPPLYCHAIN_USER_ROLE)) ||
                                            (isDomain4User(getActiveAccount(client)) && isCitationDisabledForDomainUserRole(getActiveAccount(client), DOMAIN_4_USER_ROLE))
                                        } 
                                        variant="outlined" 
                                        key={i} 
                                        title={x} 
                                        onClick={() => onCitationClicked(path)}>
                                        {`${++i}. ${x}`}
                                    </Button>
                                );
                            })}
                        </Stack>
                    </>
                )}
                {!!followupQuestions?.length && typeof followupQuestions[0] === "string" && showFollowupQuestions && onFollowupQuestionClicked && (
                    <Stack
                        direction="column"
                        flexWrap="wrap"
                        className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`}
                        spacing={1}
                        alignItems="start"
                    >
                        <Typography variant="h4">Follow-up questions:</Typography>
                        {(followupQuestions as string[]).map((x, i) => {
                            return (
                                <Button variant="outlined" color="info" key={i} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </Button>
                            );
                        })}
                    </Stack>
                )}

                {gm_verified &&
                    verifiedQuestions &&
                    verifiedQuestions.length > 0 &&
                    verifiedQuestions.map((x, i) => {
                        return (
                            <Accordion key={i} style={{ width: "100%" }}>
                                <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
                                    <Typography>{x.question}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box flexGrow={1}>
                                        <div
                                            className={styles.answerText}
                                            dangerouslySetInnerHTML={{ __html: verifiedQuestions[i].htmlParsedAnswer.answerHtml }}
                                        ></div>
                                    </Box>
                                    {!!verifiedQuestions[i].htmlParsedAnswer.citations.length && (
                                        <Stack direction="row" flexWrap="wrap" spacing={1}>
                                            <span className={styles.citationLearnMore}>Citations:</span>
                                            {verifiedQuestions[i].htmlParsedAnswer.citations.map((x, i) => {
                                                const [basePath, hash] = getCitationFilePath(x).split("#");
                                                const selectedDomain = localStorage.getItem("selectedDomain") ?? "";
                                                const path = `${basePath}?domain=${encodeURIComponent(selectedDomain)}${hash ? "#" + hash : ""}`;
                                                return (
                                                    <Button
                                                        disabled={
                                                            isNonGpscUser(getActiveAccount(client)) ||
                                                            isStreaming ||
                                                            (isSupplyChainUser(getActiveAccount(client)) && isCitationDisabledForDomainUserRole(getActiveAccount(client), SUPPLYCHAIN_USER_ROLE)) ||
                                                            (isDomain4User(getActiveAccount(client)) && isCitationDisabledForDomainUserRole(getActiveAccount(client), DOMAIN_4_USER_ROLE))
                                                        }
                                                        variant="outlined"
                                                        key={i}
                                                        className={styles.citation}
                                                        title={x}
                                                        onClick={() => onCitationClicked(path)}
                                                    >
                                                        {`${++i}. ${x}`}
                                                    </Button>
                                                );
                                            })}
                                        </Stack>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                {gm_verified ? (
                    // this was not related to my question button
                    <Stack direction="row" spacing={2} sx={{ marginTop: "20px" }}>
                        <Button
                            disabled={isStreaming}
                            variant="contained"
                            onClick={() => onFollowupQuestionClicked && onFollowupQuestionClicked(answer.choices[0]?.original_question as string)}
                        >
                            None of these are related to my question
                        </Button>
                    </Stack>
                ) : (
                    <Stack direction="row" justifyContent="end" sx={{ marginTop: "20px" }} spacing={1}>
                        <IconButton
                            disabled={isStreaming}
                            color={liked ? "success" : "inherit"}
                            title="Like"
                            aria-label="Like"
                            onClick={async () => {
                                setLiked(true);
                                setDisliked(false);
                                await handleLikeDislike(true);
                                //TODO: not any admin, only admin of that particular domain
                                // can use isDomainAdmin for this purpose;
                                // either need to pass domain id to answer component
                                // or add a conversion from domain name to id
                                // (or use a modified version of isDomainAdmin that accepts domain name and converts it)
                                if (isAdmin()) {
                                    setLikeModalOpen(true);
                                }
                            }}
                        >
                            <ThumbUp />
                        </IconButton>
                        <IconButton
                            disabled={isStreaming}
                            color={disliked ? "error" : "inherit"}
                            title="Dislike"
                            aria-label="Dislike"
                            onClick={async () => {
                                setDisliked(true);
                                setLiked(false);
                                await handleLikeDislike(false);
                                // TODO: the "General Feedback" form (for NON-Admins) flow causes for the like/dislike done on the question to be overwritten and set to 'null' when form submitted
                                // TODO: so for simplicity sake, use the same feedback form for both ADMINS and NON-ADMINS
                                setDislikeModalOpen(true);
                                /*if (isAdmin()) {
                                    setDislikeModalOpen(true);
                                } else {
                                    setGeneralFeedbackModalOpen(true);
                                }*/
                            }}
                        >
                            <ThumbDown />
                        </IconButton>
                    </Stack>
                )}
            </Stack>
            {showFeedback && <Alert className={styles.feedback}>Feedback submitted successfully!</Alert>}
        </div>
    );
};
